// UserLayout.js
import React, { useState, useEffect, useContext } from 'react';
import { Outlet, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from './AuthContext';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_PROXY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const UserLayout = () => {
  const { user } = useContext(AuthContext);
  const { customerId, userId } = useParams();

  const [dataUser, setDataUser] = useState([]);
  const [dataUsers, setDataUsers] = useState([]);
  const [error, setError] = useState(null);
  const [headerLoading, setHeaderLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const token = localStorage.getItem('token');
    const authHeaders = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    const role = user?.role_name || user?.role; // support either shape
    const canListUsers = role === 'SuperAdmin' || role === 'Admin';

    const fetchData = async () => {
      if (!user?.id) {
        // user not loaded yet
        return;
      }

      if (isMounted) {
        setHeaderLoading(true);
        setError(null);
        setDataUser([]);
        setDataUsers([]);
      }

      try {
        // ✅ Validate URL customerId (consistent behavior with CustomerLayout).
        // Admin/SuperAdmin only: /api/customer/manage is role-gated in Node, so for a plain User
        // it answers 403 ("Access denied."), which used to be mistaken for "customer not found"
        // and bounced every User off their own profile. Layout has already confirmed the customer
        // exists via the public /api/customer/:id call, so Users need no second check here.
        if (customerId && canListUsers) {
          try {
            const resp = await fetch(
              `${process.env.REACT_APP_PROXY_URL}/api/customer/manage/${customerId}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            // Treat 500 as not-found too (since your API currently returns 500 here)
            if (resp.status === 403 || resp.status === 404 || resp.status === 500) {
              if (isMounted) setError('not-found');
              return;
            }

            if (!resp.ok) {
              if (isMounted) setError('not-found');
              return;
            }

            localStorage.setItem('customer_number', customerId);
          } catch (e) {
            if (isMounted) setError('not-found');
            return;
          }
        }

        // 1) If route includes a userId, fetch that user
        if (userId) {
          try {
            const responseUser = await apiClient.get(
              `/api/user/${user.id}/getUser/${userId}`,
              { headers: authHeaders }
            );

            if (responseUser.data?.result?.error) {
              if (isMounted) setError('not-found');
              return;
            }

            if (isMounted) setDataUser(responseUser.data);
          } catch (err) {
            const status = err?.response?.status;

            if (status === 403 || status === 404) {
              if (isMounted) setError('not-found');
              return;
            }

            if (isMounted) setError('not-found');
            return;
          }
        }

        // 2) Fetch list ONLY if role allows it
        if (canListUsers) {
          try {
            const payload = {
              customer_id: String(user.customer_id),
              user_id: String(user.id),
            };

            const responseUsers = await apiClient.post(
              '/api/user/list',
              payload,
              { headers: authHeaders }
            );

            if (isMounted) setDataUsers(responseUsers.data);
          } catch (err) {
            // Keep your existing behavior: list failure doesn't force not-found
            if (isMounted) setDataUsers([]);
          }
        }
      } finally {
        // ✅ Always clears loading even if we returned early above
        if (isMounted) setHeaderLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [user, userId, customerId]);

  if (headerLoading) return null;

  if (error === 'not-found') {
    return (
      <Navigate
        to={`/customer/${customerId || localStorage.getItem('customer_number')}/not-found`}
        replace
      />
    );
  }

  return (
    <div>
      <Outlet context={{ user, dataUser, dataUsers }} />
    </div>
  );
};

export default UserLayout;